import React, { useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  Box,
  Modal,
  useModal,
  ModalClose,
  ModalHeader,
  Body2,
  Space,
  Heading5,
  Visible,
  Display,
  Overflow,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'
import { useDebounce } from 'react-use'

import { RecipeTile } from '@features/recipe-tile'
import type {
  CollectionSlug,
  CollectionUUID,
  Recipe,
  RecipeCoreId,
  RecipeUUID,
} from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { menuSearchModalOpen } from 'actions/trackingKeys'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useOfferedRecipes } from 'hooks/menu-recipes/useOfferedRecipes'
import { SuggestedRecipe } from 'routes/Menu/components/Search/SuggestedRecipe/SuggestedRecipe'
import { useBasket } from 'routes/Menu/domains/basket'
import { useMenuPreferences } from 'routes/Menu/domains/menu'
import { getBasketDate, getBasketMenuUuid } from 'selectors/basket'

import { RelevantRecipesSkeleton } from '../LoadingSkeleton/RelevantRecipesSkeleton'
import { NoResults } from '../NoResults/NoResults'
import { PopularCategories } from '../PopularCategories/PopularCategories'
import { RecipeDetail } from '../RecipeDetail/RecipeDetail'
import { SearchCategoriesGrid } from '../SearchCategoriesGrid/SearchCategoriesGrid'
import { SearchError } from '../SearchError/SearchError'
import { SearchInput } from '../SearchInput/SearchInput'
import { SearchRecipeTile } from '../SearchRecipeTile/SearchRecipeTile'
import type { TrackCTAClick } from '../types'
import { getCategoryImageUrl } from '../utils'
import { useMenuSearch } from './useMenuSearch'

import css from './styles.css'

type ViewRecipeDetails = {
  invocationId?: string
  menuId: string
  recipeId: RecipeUUID | null
  recipeRank?: number
}

export const recipeSearchModalName = 'RecipeSearchModal'

export const RecipeSearchModal = () => {
  const { closeCurrentModal } = useModal()
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [recipeDetailsId, setRecipeDetailsId] = useState<RecipeUUID | null>(null)
  const basketDate = useSelector(getBasketDate)
  const basketMenuUuid = useSelector(getBasketMenuUuid)
  const { data: menuPreferences } = useMenuPreferences()
  const offeredRecipes = useOfferedRecipes()
  const { basketHasRecipe } = useBasketRecipes()
  const { numPortions } = useNumPortions()

  useDebounce(() => setDebouncedSearchText(searchText), 500, [searchText])

  const { data, error, isLoading, retry } = useMenuSearch({
    deliveryDate: basketDate,
    menuId: basketMenuUuid,
    searchText: debouncedSearchText,
  })

  const { addRecipe, removeRecipe, reachedLimit, canAddRecipes } = useBasket()
  const currentCollectionId = useCurrentCollectionId()

  const cleanUpState = () => {
    setSearchText('')
    setRecipeDetailsId(null)
  }

  const viewRecipeDetails = ({ invocationId, menuId, recipeId, recipeRank }: ViewRecipeDetails) => {
    setRecipeDetailsId(recipeId)
    queueTrackingEvent<{
      action: 'view_recipe_details'
      properties: {
        menu_id: string
        recipe_id: string
        search_invocation_id?: string
        search_rank?: number
      }
    }>({
      action: 'view_recipe_details',
      properties: {
        menu_id: menuId,
        recipe_id: recipeId || '',
        ...(invocationId && { search_invocation_id: invocationId }),
        ...(recipeRank && { search_rank: recipeRank }),
      },
    })
    datadogRum.addAction('open_recipe_details', {
      menu_id: menuId,
      recipe_id: recipeId || '',
      source: 'search',
    })
  }

  const viewSearch = ({ menuId }: { menuId: string }) => {
    setRecipeDetailsId(null)
    queueTrackingEvent<{
      action: typeof menuSearchModalOpen
      properties: {
        menu_id: string
      }
    }>({
      action: menuSearchModalOpen,
      properties: {
        menu_id: menuId,
      },
    })
    datadogRum.addAction('open_search', { menu_id: menuId })
  }

  return (
    <Modal
      className={css.RecipeSearchModal}
      name={recipeSearchModalName}
      onRequestClose={closeCurrentModal}
      onAfterClose={cleanUpState}
      overlayClassName={recipeDetailsId ? css.overlay : ''}
    >
      {recipeDetailsId ? (
        <RecipeDetail
          onClose={() => viewSearch({ menuId: basketMenuUuid })}
          onSelectRecipe={setRecipeDetailsId}
          recipeId={recipeDetailsId}
        />
      ) : (
        <ModalHeader>
          <Box paddingRight={[4, 12]}>
            <SearchInput onChange={setSearchText} value={searchText} />
          </Box>
          <ModalClose onClose={closeCurrentModal} />
        </ModalHeader>
      )}
      <Box
        display={recipeDetailsId ? Display.None : Display.Initial}
        overflowY={Overflow.Scroll}
        paddingV={[4, 6]}
        paddingH={[4, 6]}
      >
        <Box paddingH={2} paddingBottom={4}>
          {(() => {
            if (error) {
              return <SearchError onRetry={retry} />
            } else if (data) {
              const { categories, recipes, invocation_id: invocationId } = data

              return (
                <Box>
                  <Box paddingBottom={6}>
                    {recipes.length > 0 ? (
                      <>
                        <Heading5>Relevant Recipes</Heading5>
                        <Body2>On this week&apos;s menu</Body2>
                        <Space size={4} />
                        {recipes.map((recipe, index) => {
                          const trackingData = {
                            invocationId,
                            menuId: basketMenuUuid,
                            index,
                          }

                          const offeredRecipe: Recipe | null =
                            offeredRecipes[recipe.id as RecipeUUID] ?? null

                          const searchTrackingData = {
                            search_invocation_id: invocationId,
                            search_rank: index,
                            menu_id: basketMenuUuid,
                            add_type: 'search',
                            event_screen: 'menu_search_screen',
                            recipe_id: recipe.id,
                          }

                          return (
                            <React.Fragment key={recipe.id}>
                              <Visible display={[Display.None, Display.Block]}>
                                {offeredRecipe && (
                                  <Box paddingBottom={2}>
                                    <SearchRecipeTile
                                      addRecipe={(recipeId) =>
                                        addRecipe(recipeId, 'menu_search_screen')
                                      }
                                      menuPreferences={menuPreferences}
                                      onClick={() =>
                                        viewRecipeDetails({
                                          invocationId,
                                          menuId: basketMenuUuid,
                                          recipeId: offeredRecipe?.id || null,
                                          recipeRank: index,
                                        })
                                      }
                                      reachedLimit={reachedLimit}
                                      recipe={offeredRecipe}
                                      removeRecipe={removeRecipe}
                                      trackingData={trackingData}
                                    />
                                  </Box>
                                )}
                              </Visible>
                              <Visible display={[Display.Block, Display.None]}>
                                {offeredRecipe && (
                                  <Box paddingBottom={4}>
                                    <RecipeTile
                                      recipe={offeredRecipe}
                                      addRecipe={(recipeId) => {
                                        queueTrackingEvent<TrackCTAClick>({
                                          action: 'add_recipe',
                                          properties: searchTrackingData,
                                        })
                                        addRecipe(recipeId, 'menu_search_screen')
                                      }}
                                      alternativeOptions={{ optionsType: 'none', options: [] }}
                                      canAddRecipes={canAddRecipes}
                                      currentCollectionId={currentCollectionId}
                                      menuId={basketMenuUuid}
                                      isInBasket={basketHasRecipe(offeredRecipe.coreId)}
                                      isOutOfStock={false}
                                      onClick={() =>
                                        viewRecipeDetails({
                                          invocationId,
                                          menuId: basketMenuUuid,
                                          recipeId: offeredRecipe.id,
                                          recipeRank: index,
                                        })
                                      }
                                      originalId={recipe.core_recipe_id as RecipeCoreId}
                                      reachedLimit={reachedLimit}
                                      recipeReference=""
                                      removeRecipe={(recipeId) => {
                                        queueTrackingEvent<TrackCTAClick>({
                                          action: 'remove_recipe',
                                          properties: searchTrackingData,
                                        })
                                        removeRecipe(recipeId)
                                      }}
                                      selectVariant={() => {}}
                                      setBrowserCTAVisible={() => {}}
                                      menuPreferences={menuPreferences}
                                      insideOverflow
                                      numPortions={numPortions}
                                    />
                                  </Box>
                                )}
                              </Visible>
                            </React.Fragment>
                          )
                        })}
                      </>
                    ) : (
                      <NoResults />
                    )}
                  </Box>
                  {categories.length > 0 && (
                    <Box paddingBottom={4}>
                      <SearchCategoriesGrid
                        categories={categories.map((category) => ({
                          id: category.id as CollectionUUID,
                          slug: category.slug as CollectionSlug,
                          imageUrl: getCategoryImageUrl(category.id),
                          name: category.short_title,
                          recipeCount: category.recipe_count,
                        }))}
                        title="Related Categories"
                      />
                    </Box>
                  )}
                </Box>
              )
            } else if (isLoading) {
              return <RelevantRecipesSkeleton />
            } else {
              return (
                <>
                  <PopularCategories />
                  <Space size={4} />
                  <SuggestedRecipe
                    addRecipe={addRecipe}
                    canAddRecipes={canAddRecipes}
                    menuPreferences={menuPreferences}
                    onClick={(recommendedRecipeId) =>
                      viewRecipeDetails({
                        menuId: basketMenuUuid,
                        recipeId: recommendedRecipeId,
                      })
                    }
                    reachedLimit={reachedLimit}
                    removeRecipe={removeRecipe}
                  />
                </>
              )
            }
          })()}
        </Box>
      </Box>
    </Modal>
  )
}
